// @ts-nocheck
import { useEffect } from 'react';
import * as React from 'react';
import {
  getCachedToken,
  getOpenHouseToken,
  isLatestTradingDay,
} from '../../../util';
import { decode } from '@msgpack/msgpack';
import { StreamType, CandleTupleSTOHLC } from 'types';
import { dayjs } from 'util/shared/date';
import useLog from '../../useLog';
import { STREAM_HOST, STREAM_HOST_URL } from 'config';

const MIN_REOPEN_RETRY_PERIOD_MS = 1_000;
const MAX_REOPEN_RETRY_PERIOD_MS = 5 * 60 * 1_000;
const MAX_REOPEN_RETRIES = Math.round(
  (12 * 60 * 60 * 1_000) / MAX_REOPEN_RETRY_PERIOD_MS,
);

export interface SubAction {
  action: 'subscribe' | 'unsubscribe' | 'unsubscribe_all';
  underlyings?: string[];
  stream_types?: number;
}

interface HiroStreamingRefs {
  socket: React.MutableRefObject<WebSocket | null>;
  socketRetries: React.MutableRefObject<number>;
  currentSymbol: React.RefObject<string>;
}

export type SendStreamControlMsgFn = (msg: SubAction) => void;

export interface UseStreamingSocketParams {
  enabled: boolean;
  hiroStreamingRefs: HiroStreamingRefs;
  updateCandleCallback: (
    candle: CandleTupleSTOHLC,
    streamType: StreamType,
  ) => void;
  streamingSubscribeBitmask: number;
  endDate?: dayjs.Dayjs;
  token?: string;
}

export interface UseStreamingSocketResult {
  fetchStreamingEndpoint: (endpoint: string) => Promise<Response>;
  reconnectSocket: () => void;
  sendStreamControlMsg: SendStreamControlMsgFn;
}

export const useStreamingSocket = ({
  enabled,
  hiroStreamingRefs,
  updateCandleCallback,
  streamingSubscribeBitmask,
  endDate,
  token,
}: UseStreamingSocketParams): UseStreamingSocketResult => {
  const { nonProdDebugLog } = useLog('useStreamingSocket');
  const socketSubscribeToSym = (
    sendStreamControlMsg: SendStreamControlMsgFn,
  ) => {
    nonProdDebugLog('Subscribing to ', hiroStreamingRefs.currentSymbol.current);
    const msg = {
      action: 'subscribe',
      underlyings: [hiroStreamingRefs.currentSymbol.current],
      stream_types: streamingSubscribeBitmask,
    };
    sendStreamControlMsg(msg);
  };

  return useStreamingSocketCore({
    enabled,
    socket: hiroStreamingRefs.socket,
    socketRetries: hiroStreamingRefs.socketRetries,
    onOpen: socketSubscribeToSym,
    onReconnect: socketSubscribeToSym,
    updateCandleCallback,
    endDate,
    token,
  });
};

interface UseStreamingSocketCoreParams {
  enabled: boolean;
  socket: React.MutableRefObject<WebSocket | null>;
  socketRetries: React.MutableRefObject<number>;
  onOpen?: (sendStreamControlMsg: SendStreamControlMsgFn) => void;
  onReconnect?: (sendStreamControlMsg: SendStreamControlMsgFn) => void;
  updateCandleCallback: (
    candle: CandleTupleSTOHLC,
    streamType: StreamType,
  ) => void;
  endDate?: dayjs.Dayjs;
  token?: string;
}

export const useStreamingSocketCore = ({
  enabled,
  socket,
  socketRetries,
  onOpen,
  onReconnect,
  updateCandleCallback,
  endDate,
  token,
}: UseStreamingSocketCoreParams): UseStreamingSocketResult => {
  const { nonProdDebugLog } = useLog('useStreamingSocketCore');

  const fetchStreamingEndpoint = async (endpoint: string) => {
    const url = `${STREAM_HOST_URL}${endpoint}`;
    nonProdDebugLog('Fetching', url);
    // Prefer open-house over cached token in case user is Pro or under
    // TODO: Use getAuthHeader instead
    const bearerToken = token ?? getOpenHouseToken() ?? getCachedToken();
    return await fetch(url, {
      headers: { Authorization: `Bearer ${bearerToken}` },
    });
  };

  const sendStreamControlMsg = (msg: SubAction) => {
    if (socket.current?.readyState !== WebSocket.OPEN) {
      nonProdDebugLog('Attempted to send msg but websocket is not open', msg);
      return;
    }

    socket.current.send(JSON.stringify(msg));
  };

  const onOpenWrapper = (_evt: Event) => {
    nonProdDebugLog(
      '%c Websocket connected. Subscribing...',
      'background: #222; color: #bada55',
    );
    if (onOpen != null) {
      onOpen(sendStreamControlMsg);
    }
  };

  const onMessage = async (event: MessageEvent) => {
    const data = event.data as string | Blob;
    if (typeof data === 'string') {
      console.warn('Ack from server: ', data);
    } else {
      const [streamType, candle] = decode(await data.arrayBuffer(), {
        useBigInt64: true,
      }) as [StreamType, CandleTupleSTOHLC];
      // console.log(`onMessage event ${streamTypeEnumName(stream)}`, signal);
      updateCandleCallback(candle, streamType);
    }
  };

  const onError = (event: Event) => {
    // the event does not give us any information that is useful unfortunately
    console.error('useStreamingSocketCore error', event);
  };

  const onClose = (event: Event) => {
    if (!enabled) {
      return;
    }

    const retries = socketRetries.current;

    if (retries >= MAX_REOPEN_RETRIES) {
      // TODO - surface error to user via UX since the chart will be stalled
      console.warn(
        'Socket closed. Reached maximum number of retries to re-open, not restarting.',
        event,
      );
      return;
    }

    console.warn(
      `Socket closed. Retry #${retries + 1} - restarting in 1 second...`,
      event,
    );
    socketRetries.current = retries + 1;
    setTimeout(
      () => {
        if (!isSocketOpen()) {
          void authenticateAndConnect(false);
        }
      },
      Math.min(
        Math.max(
          MIN_REOPEN_RETRY_PERIOD_MS,
          Math.pow(2, retries) * MIN_REOPEN_RETRY_PERIOD_MS,
        ),
        MAX_REOPEN_RETRY_PERIOD_MS,
      ),
    );
  };
  const isSocketOpen = () => {
    return socket.current?.readyState === WebSocket.OPEN;
  };

  const cleanupSocket = (
    socketRef: React.MutableRefObject<WebSocket | null>,
  ) => {
    return () => {
      nonProdDebugLog('Clearing out socket');
      if (socketRef.current != null) {
        socketRef.current.removeEventListener('open', onOpenWrapper);
        socketRef.current.removeEventListener(
          'message',
          onMessage as () => void,
        );
        socketRef.current.removeEventListener('error', onError);
        socketRef.current.removeEventListener('close', onClose);
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  };

  const authenticateAndConnect = (isNewConnection = true) => {
    nonProdDebugLog('connecting socket...');

    const streamingToken = token ?? getOpenHouseToken() ?? getCachedToken();
    if (streamingToken == null) {
      nonProdDebugLog('Found null streamingToken in authenticateAndConnect');
      return;
    }

    if (socket.current != null) {
      nonProdDebugLog('found old socket, clearing...');
      cleanupSocket(socket)();
    }
    if (isNewConnection) {
      socketRetries.current = 0;
    }
    socket.current = new WebSocket(
      `wss://${STREAM_HOST}/stream?token=${encodeURIComponent(streamingToken)}`,
    );

    socket.current.addEventListener('open', onOpenWrapper);
    socket.current.addEventListener('message', onMessage as () => void);
    socket.current.addEventListener('error', onError);
    socket.current.addEventListener('close', onClose);

    nonProdDebugLog('Created Socket');
  };

  const reconnectSocket = () => {
    if (!enabled) {
      return;
    }

    if (isSocketOpen()) {
      sendStreamControlMsg({
        action: 'unsubscribe_all',
      });
      if (onReconnect != null) {
        onReconnect(sendStreamControlMsg);
      }
    } else if (endDate == null || isLatestTradingDay(endDate)) {
      void authenticateAndConnect();
    }

    return cleanupSocket(socket);
  };

  const closeSocket = () => {
    if (isSocketOpen()) {
      nonProdDebugLog('closing socket...');
      sendStreamControlMsg({
        action: 'unsubscribe_all',
      });
      cleanupSocket(socket)();
    }
  };

  useEffect(
    () => {
      // in case enabled state changes, close any open socket
      if (!enabled) {
        closeSocket();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled],
  );

  return {
    fetchStreamingEndpoint,
    reconnectSocket,
    sendStreamControlMsg,
  };
};
